import { ApolloClient, HttpLink, InMemoryCache, from } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { createApolloProvider } from "@vue/apollo-option";
import getEnv, { getTestMode } from "./env";
import { store } from "./store/store";
import { AuthServices } from "./services/auth.services";
import NProgress from "nprogress";
import { onError } from "@apollo/client/link/error";
import { GraphQLError } from "graphql";

NProgress.configure({ showSpinner: false });

const env = getEnv();

const httpEndpoint = env.VUE_APP_GRAPHQL_HTTP;

const authLink = setContext(async (_, { headers }) => {
    const xData = {
        user: store.state.user.headerUser,
        company:
            !store.state.inAdmin && store.state.selected_company
                ? {
                      // name: store.state.selected_company.name,
                      accountingType:
                          store.state.selected_company.profile?.accountingType ||
                          "RECEIVABLES_DEBT",
                      taxSystem: store.state.selected_company.taxSystem || "BIC_Normal",
                      taxSystemType: store.state.selected_company.taxSystemType || "IS",
                  }
                : undefined,
        accountingPeriodId:
            store.state.accountingPeriod?.isMandatory &&
            store.state.accountingPeriod?.selectedAccountingPeriod?.id
                ? store.state.accountingPeriod.selectedAccountingPeriod.id
                : "",
    };
    const Authorization = await AuthServices.getUserToken();
    const authorizationHeader = Authorization ? { Authorization } : {};

    const additionalHeader = {
        "X-userId":
            (store.state.user.isSubUserOf
                ? store.state.user.isSubUserOf.id
                : store.state.user?.id) || "",
        "X-subUserId": store.state.user.isSubUserOf ? store.state.user.id : "",
        "X-companyId": !store.state.inAdmin ? store.state.selected_company?.id || "" : "",
        "X-accountingFirmId": store.state.inAdmin
            ? store.state.user?.company?.id || ""
            : store.state.companyAccountingFirm
              ? store.state.companyAccountingFirm.id
              : "",
        // "X-accountingPeriodId": store.state.accountingPeriod?.isMandatory && store.state.accountingPeriod?.selectedAccountingPeriod?.id ? store.state.accountingPeriod.selectedAccountingPeriod.id : "",
        "X-data": JSON.stringify(xData),
    };
    return {
        headers: {
            ...headers,
            ...authorizationHeader,
            ...additionalHeader,
        },
    };
});

let counter: number = 0;

const httpLink = new HttpLink({
    uri: httpEndpoint,
    fetch: async (uri, options) => {
        if (!getTestMode()) {
            counter++;
            NProgress.start();
            try {
                const response = await fetch(uri, options);
                counter--;
                if (counter <= 0) {
                    NProgress.done();
                }
                return response;
            } catch (error) {
                counter--;
                if (counter <= 0) {
                    NProgress.done();
                }
                throw error;
            }
        }
        return fetch(uri, options);
    },
});

const errorLink = onError(({ operation, networkError, graphQLErrors }) => {
    // bobbeeLink.remove(operation.operationName);
    console.error("onError started operation.operationName= ", operation.operationName);
    if (operation.query) {
        console.error(
            operation.query ? JSON.stringify(operation.query.definitions[0]) : "not query",
            graphQLErrors && graphQLErrors.length ? graphQLErrors[0] : "",
            networkError,
        );
    }
    const isLoggedInUser =
        operation.query?.definitions.length &&
        "selectionSet" in operation.query.definitions[0] &&
        "selections" in operation.query.definitions[0].selectionSet &&
        "name" in operation.query.definitions[0].selectionSet.selections[0] &&
        operation.query.definitions[0].selectionSet.selections[0].name?.value === "loggedInUser";

    const isRecoverPassword =
        operation.query?.definitions.length &&
        "selectionSet" in operation.query.definitions[0] &&
        "selections" in operation.query.definitions[0].selectionSet &&
        "name" in operation.query.definitions[0].selectionSet.selections[0] &&
        operation.query.definitions[0].selectionSet.selections[0].name?.value ===
            "sendRecoverPasswordLink";

    const isRecoveryUser =
        operation.query?.definitions.length &&
        "name" in operation.query.definitions[0] &&
        operation.query.definitions[0].name?.value === "associatedUser";

    const isAccessForbidden =
        graphQLErrors?.length &&
        graphQLErrors.some((e: GraphQLError) => {
            const originalError = e.extensions?.originalError as { message?: string } | undefined;
            return originalError?.message === "Access forbidden"; // Fix typo: "forbiden" -> "forbidden"
        });
    if (
        !isRecoveryUser &&
        !isRecoverPassword &&
        !isLoggedInUser &&
        graphQLErrors?.some((e: GraphQLError) => e.extensions?.code === "AUTH_ERROR")
    ) {
        console.error("AuthError - location reload");
    }

    if (isAccessForbidden) {
        // Check if user has access rights to the page
        console.error("Access forbidden - location reload");
    }

    // console.log("before", errorApollo)
    const errorApollo = {
        request: operation.operationName,
        messages: graphQLErrors
            ? graphQLErrors.map(
                  (x) => x?.extensions?.originalError?.["message"] || x.message || "ERROR",
              )
            : "XXX",
        type: "error",
        errorType: "Apollo",
    };

    console.log(
        "%cError",
        "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;",
        graphQLErrors?.[0]?.extensions?.originalError?.["message"],
    );

    store.commit("setApolloErrors", errorApollo);
});

const cache = new InMemoryCache();

export const apolloClient = new ApolloClient({
    link: from([errorLink, authLink.concat(httpLink)]),
    cache,
    defaultOptions: {
        watchQuery: {
            fetchPolicy: "network-only",
        },
    },
});

export const apolloProvider = createApolloProvider({
    defaultClient: apolloClient,
});

export async function clearStore() {
    try {
        if (apolloClient) {
            await apolloClient.clearStore();
        }
    } catch (e) {
        console.error("%cError on cache reset (logout)", "color: orange;", e.message);
    }
}
